import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEOHead } from '@/components/SEOHead';
import { formatPrice } from '@/context/AppContext';
import axios from 'axios';
import {
  CreditCard, Globe, Lock, Shield, Loader2, AlertCircle,
  ArrowRight, Check, Wallet, Smartphone, QrCode, Clock,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OfferCheckout() {
  const { offerId } = useParams();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payLoading, setPayLoading] = useState(false);
  const [error, setError] = useState('');
  const [gateway, setGateway] = useState('paymongo');

  useEffect(() => {
    const fetchOffer = async () => {
      try {
        const resp = await axios.get(`${API}/offers/${offerId}`);
        setOffer(resp.data);
      } catch (err) {
        setError('Offer not found or expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchOffer();
    // axios and API are module-level constants
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId]);

  const handlePayMongo = async () => {
    setPayLoading(true);
    setError('');
    try {
      const resp = await axios.post(`${API}/checkout/offer/${offerId}/paymongo`);
      window.location.href = resp.data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment setup failed.');
      setPayLoading(false);
    }
  };

  const handlePayPal = async () => {
    setPayLoading(true);
    setError('');
    try {
      const resp = await axios.post(`${API}/checkout/offer/${offerId}/paypal`);
      // For PayPal Smart Buttons we'd need the popup flow, but for custom offers we redirect
      setError('PayPal order created. Smart Buttons integration for custom offers coming soon.');
      setPayLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'PayPal setup failed.');
      setPayLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand-blue animate-spin" />
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground mb-2">Offer Not Found</h2>
          <p className="text-sm text-muted-foreground mb-4">This offer may have expired or been removed.</p>
          <Link to="/services" className="text-brand-blue font-semibold">Browse Services</Link>
        </div>
      </div>
    );
  }

  const billingLabel = offer.billing_cycle === 'one-time' ? 'One-Time Payment' : `${offer.billing_cycle.charAt(0).toUpperCase() + offer.billing_cycle.slice(1)} Billing`;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-20" data-testid="offer-checkout-page">
      <SEOHead title={`Checkout: ${offer.title} | MediaLabs`} path={`/checkout/${offerId}`} />
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-orange/15 to-brand-orange/5 flex items-center justify-center">
              <Lock className="w-5 h-5 text-brand-orange" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900">Custom Offer Checkout</h3>
              <p className="text-[11px] text-slate-400 flex items-center gap-1"><Shield className="w-3 h-3" /> SSL encrypted</p>
            </div>
          </div>

          <div className="p-6">
            {/* Offer Summary */}
            <div className="bg-slate-50/80 rounded-xl p-4 mb-4">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Custom Offer</p>
              <h4 className="text-base font-bold text-slate-900" data-testid="offer-title">{offer.title}</h4>
              <p className="text-xs text-slate-500 mt-1">{offer.description}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="inline-flex items-center gap-1 text-[11px] text-slate-400"><Clock className="w-3 h-3" /> {billingLabel}</span>
              </div>
            </div>

            {/* Price */}
            <div className="rounded-xl border border-brand-orange/20 bg-gradient-to-br from-brand-orange/[0.04] to-transparent p-4 mb-5">
              <p className="text-[10px] font-bold text-brand-orange uppercase tracking-widest mb-2">Amount Due</p>
              <span className="text-3xl font-extrabold text-slate-900">${offer.price_usd?.toLocaleString()}</span>
              {offer.billing_cycle !== 'one-time' && <span className="text-sm text-slate-400 ml-1">/{offer.billing_cycle === 'weekly' ? 'wk' : 'mo'}</span>}
            </div>

            {/* Gateway — PayMongo only (PayPal temporarily disabled) */}
            <div className="flex gap-1 p-1 bg-slate-100 rounded-xl mb-4">
              <div className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-semibold bg-white text-slate-900 shadow-sm">
                <CreditCard className="w-4 h-4" /> Cards & E-Wallets
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-100 mb-4">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                <p className="text-xs text-red-600">{error}</p>
              </div>
            )}

            {/* Pay buttons */}
            {gateway === 'paymongo' && (
              <div>
                <button onClick={handlePayMongo} disabled={payLoading}
                  className="w-full py-4 rounded-xl bg-gradient-to-r from-brand-orange to-brand-orange-dark text-white text-sm font-bold shadow-lg shadow-brand-orange/25 hover:shadow-xl transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2">
                  {payLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : <><Lock className="w-4 h-4" /> Pay ${offer.price_usd?.toLocaleString()}</>}
                </button>
                <div className="flex items-center justify-center gap-2 mt-3">
                  {['Visa', 'GCash', 'Maya', 'QR Ph'].map(m => (
                    <span key={m} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-slate-200 text-[10px] font-medium text-slate-500 shadow-sm">{m}</span>
                  ))}
                </div>
              </div>
            )}
            {gateway === 'paypal' && (
              <button onClick={handlePayPal} disabled={payLoading}
                className="w-full py-4 rounded-xl bg-[#FFC439] text-slate-900 text-sm font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {payLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : <><Globe className="w-4 h-4" /> Pay with PayPal</>}
              </button>
            )}

            <div className="flex items-center justify-center gap-5 mt-5 text-[10px] text-slate-400">
              <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> SSL Secure</span>
              <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> PCI Compliant</span>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
